import React, { useCallback, useEffect, useState } from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import { Container } from "./styles";
import DataTable from "react-data-table-component";
import "react-data-table-component-extensions/dist/index.css";
import { useModalContext } from "../../contexts/ModalContext";
import ClientPointsModal from "./ClientPointsModal";
import * as ClientsService from "../../services/clients";
import * as ClientPointsService from "../../services/clientPoints";
import * as MechanicPointsConfigService from "../../services/mechanicPointsConfig";
import { useAuth } from "../../hooks/useAuth";
import { Typography } from "@mui/material";
import StarsIcon from "@mui/icons-material/Stars";
import FilterComponent from "../../components/ui/Filter";

var moment = require("moment");

const emptyConfig = {
    pointsPerRealSpent: "",
    maxPointsDiscountPerSale: "",
};

const CHANGE_TYPE_META = {
    CREATED: { label: "Criado", color: "info" },
    UPDATED: { label: "Atualizado", color: "primary" },
    DELETED: { label: "Removido", color: "default" },
    EARNED: { label: "Pontuado", color: "success" },
    REDEEMED: { label: "Resgatado", color: "secondary" },
    REDEMPTION_CODE_GENERATED: { label: "Código gerado", color: "warning" },
    REDEMPTION_FAILED: { label: "Resgate recusado", color: "error" },
};

function parseApiError(error, fallback = "Operação não pôde ser concluída.") {
    const data = error?.response?.data;
    if (typeof data === "string") return data;
    if (data?.message) return data.message;
    if (Array.isArray(data?.errors)) return data.errors.map((e) => e.defaultMessage || e.message).filter(Boolean).join(" ");
    return fallback;
}

function normalizePageResponse(response) {
    if (Array.isArray(response)) {
        return { content: response, totalElements: response.length };
    }
    return {
        content: Array.isArray(response?.content) ? response.content : [],
        totalElements: response?.totalElements || 0,
    };
}

function getLogMessage(row) {
    if (row.message) return row.message;
    if (row.changeType === "REDEMPTION_FAILED") {
        return "Nao e possivel usar pontos em uma ordem de servico que ja possui desconto.";
    }
    return "—";
}

const ClientPoints = () => {
    const { setModal } = useModalContext();
    const [data, setData] = useState([]);
    const [pointsMap, setPointsMap] = useState({});
    const { loggedMechanic } = useAuth();

    const [loading, setLoading] = useState(false);
    const [totalRows, setTotalRows] = useState(0);
    const [page, setPage] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [sortDirection, setSortDirection] = useState("ASC");
    const [sortBy, setSortBy] = useState("name");
    const [filterText, setFilterText] = useState("");

    const [logData, setLogData] = useState([]);
    const [logLoading, setLogLoading] = useState(false);
    const [logTotalRows, setLogTotalRows] = useState(0);
    const [logPage, setLogPage] = useState(0);
    const [logPerPage, setLogPerPage] = useState(10);
    const [config, setConfig] = useState(emptyConfig);
    const [configLoading, setConfigLoading] = useState(false);
    const [configSaving, setConfigSaving] = useState(false);
    const [clientNameMap, setClientNameMap] = useState({});

    const fetchConfig = useCallback(async () => {
        setConfigLoading(true);
        try {
            const response = await MechanicPointsConfigService.getMechanicPointsConfig(loggedMechanic.id);
            setConfig({
                pointsPerRealSpent: response.pointsPerRealSpent ?? "",
                maxPointsDiscountPerSale: response.maxPointsDiscountPerSale ?? "",
            });
        } catch (err) {
            setConfig(emptyConfig);
        } finally {
            setConfigLoading(false);
        }
    }, [loggedMechanic?.id]);

    function handleConfigChange(event) {
        const { name, value } = event.target;
        setConfig((current) => ({ ...current, [name]: value }));
    }

    async function handleConfigSubmit(event) {
        event.preventDefault();
        const request = {
            pointsPerRealSpent: Number(config.pointsPerRealSpent),
            maxPointsDiscountPerSale: Number(config.maxPointsDiscountPerSale),
        };

        const hasInvalidValue = Object.values(request).some((value) => Number.isNaN(value) || value < 0);
        if (hasInvalidValue) {
            alert("Informe apenas valores numericos maiores ou iguais a zero.");
            return;
        }

        setConfigSaving(true);
        try {
            const response = await MechanicPointsConfigService.saveMechanicPointsConfig(loggedMechanic.id, request);
            setConfig({
                pointsPerRealSpent: response.pointsPerRealSpent ?? "",
                maxPointsDiscountPerSale: response.maxPointsDiscountPerSale ?? "",
            });
            alert("Configuracao de pontos salva com sucesso.");
        } catch (err) {
            alert(parseApiError(err, "Nao foi possivel salvar a configuracao de pontos."));
        } finally {
            setConfigSaving(false);
        }
    }

    const fetchPointsForClients = useCallback(async (clients) => {
        const newPointsMap = {};
        const newNameMap = {};
        await Promise.all(
            clients.map(async (client) => {
                newNameMap[client.id] = client.name;
                try {
                    const pts = await ClientPointsService.getPointsByMechanic(client.id, loggedMechanic.id);
                    newPointsMap[client.id] = pts.points;
                } catch {
                    newPointsMap[client.id] = null;
                }
            })
        );
        setPointsMap((prev) => ({ ...prev, ...newPointsMap }));
        setClientNameMap((prev) => ({ ...prev, ...newNameMap }));
    }, [loggedMechanic?.id]);

    const fetchTableData = useCallback(async () => {
        setLoading(true);
        try {
            const response = await ClientsService.getClientsByMechanicId(
                loggedMechanic.id,
                page,
                perPage,
                sortBy,
                sortDirection,
                filterText
            );
            setData(response.content);
            setTotalRows(response.totalElements);
            fetchPointsForClients(response.content);
        } finally {
            setLoading(false);
        }
    }, [fetchPointsForClients, filterText, loggedMechanic?.id, page, perPage, sortBy, sortDirection]);

    const fetchLogData = useCallback(async () => {
        setLogLoading(true);
        try {
            const response = await ClientPointsService.getLogsByMechanic(loggedMechanic.id, logPage, logPerPage);
            const normalized = normalizePageResponse(response);
            setLogData(normalized.content);
            setLogTotalRows(normalized.totalElements);
        } finally {
            setLogLoading(false);
        }
    }, [loggedMechanic?.id, logPage, logPerPage]);

    useEffect(() => {
        if (loggedMechanic?.id) fetchTableData();
    }, [fetchTableData, loggedMechanic?.id]);

    useEffect(() => {
        if (loggedMechanic?.id) fetchLogData();
    }, [fetchLogData, loggedMechanic?.id]);

    useEffect(() => {
        if (loggedMechanic?.id) fetchConfig();
    }, [fetchConfig, loggedMechanic?.id]);

    const handlePageChange = (newPage) => {
        setPage(newPage - 1);
    };

    const handlePerRowsChange = async (newPerPage) => {
        setPerPage(newPerPage);
    };

    const handleSortChange = async (column, sortDirection) => {
        setSortBy(column.id);
        setSortDirection(sortDirection.toUpperCase());
    };

    const columns = [
        {
            id: "name",
            name: "Nome",
            selector: (row) => row.name,
            sortable: true,
        },
        {
            name: "Email",
            selector: (row) => row.email,
        },
        {
            name: "Telefone",
            selector: (row) => row.phone,
        },
        {
            name: "Pontos",
            center: true,
            cell: (row) => {
                const pts = pointsMap[row.id];
                if (pts == null) return <Chip label="—" size="small" variant="outlined" />;
                return <Chip label={pts} size="small" color="primary" />;
            },
        },
        {
            name: "Ações",
            minWidth: "150px",
            cell: (row) => (
                <Button
                    variant="outlined"
                    size="small"
                    sx={{ whiteSpace: "nowrap", minWidth: "auto" }}
                    onClick={() =>
                        setModal(
                            <ClientPointsModal
                                client={row}
                                mechanicId={loggedMechanic.id}
                                onUpdate={() => { fetchTableData(); fetchLogData(); }}
                            />
                        )
                    }
                >
                    <StarsIcon fontSize="small" sx={{ mr: 0.5 }} />
                    Gerenciar
                </Button>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
        },
    ];

    const logColumns = [
        {
            name: "Data",
            selector: (row) => row.createdAt ? moment(row.createdAt).format("DD/MM/YYYY HH:mm") : "—",
            sortable: true,
            minWidth: "150px",
        },
        {
            name: "Usuario",
            selector: (row) => clientNameMap[row.userId] || `ID ${row.userId}`,
            minWidth: "160px",
        },
        {
            name: "Tipo",
            minWidth: "160px",
            cell: (row) => {
                const meta = CHANGE_TYPE_META[row.changeType] || { label: row.changeType || "—", color: "default" };
                return <Chip label={meta.label} size="small" color={meta.color} variant={meta.color === "default" ? "outlined" : "filled"} />;
            },
        },
        {
            name: "Pontos anteriores",
            selector: (row) => row.previousPoints,
            center: true,
            minWidth: "150px",
        },
        {
            name: "Pontos novos",
            selector: (row) => row.newPoints,
            center: true,
            minWidth: "130px",
        },
        {
            name: "Responsavel",
            selector: (row) => row.changedByEmail || "—",
            minWidth: "190px",
        },
        {
            name: "Mensagem",
            selector: (row) => getLogMessage(row),
            wrap: true,
            minWidth: "280px",
        },
    ];

    const logConditionalRowStyles = [
        {
            when: (row) => row.changeType === "REDEMPTION_FAILED",
            style: {
                backgroundColor: "#fff1f1",
                color: "#8a1f1f",
            },
        },
    ];

    const subHeaderComponentMemo = React.useMemo(() => {
        const handleClear = () => {
            if (filterText) {
                setFilterText("");
            }
        };

        return (
            <FilterComponent
                onFilter={(e) => setFilterText(e.target.value)}
                onClear={handleClear}
                filterText={filterText}
                placeholder="Filtrar por Nome"
            />
        );
    }, [filterText]);

    return (
        <Container>
            <Box
                mb={1}
                fontWeight="bold"
                color="#666666"
                fontSize={{ xs: "h7.fontSize", sm: "h6.fontSize", md: "h5.fontSize" }}
            >
                Pontos de Clientes
            </Box>
            <Box
                mb={3}
                color="#666666"
                fontSize={{ xs: "body2.fontSize", sm: "body1.fontSize" }}
            >
                Configure como seus clientes acumulam pontos, acompanhe saldos e audite resgates recebidos pela integracao.
            </Box>
            <Box mb={4}>
                <Card>
                    <Box p={3}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} mb={2}>
                            <Typography variant="h6" color="#666666">
                                Configuracao de pontos
                            </Typography>
                            {configLoading && <CircularProgress size={24} />}
                        </Box>
                        <Alert severity="info" sx={{ mb: 2 }}>
                            O codigo de resgate e gerado no app do cliente. A mecanica apenas visualiza os logs e os dados de resgate
                            recebidos pela integracao.
                        </Alert>
                        <form onSubmit={handleConfigSubmit}>
                            <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "repeat(2, 1fr)" }} gap={2}>
                                <TextField
                                    name="pointsPerRealSpent"
                                    label="Pontos por real gasto"
                                    type="number"
                                    value={config.pointsPerRealSpent}
                                    onChange={handleConfigChange}
                                    inputProps={{ min: 0, step: "any" }}
                                    required
                                    fullWidth
                                />
                                <TextField
                                    name="maxPointsDiscountPerSale"
                                    label="Limite maximo de pontos por OS"
                                    type="number"
                                    value={config.maxPointsDiscountPerSale}
                                    onChange={handleConfigChange}
                                    inputProps={{ min: 0, step: "any" }}
                                    required
                                    fullWidth
                                />
                            </Box>
                            <Box display="flex" justifyContent="flex-end" mt={2}>
                                <Button variant="contained" type="submit" disabled={configLoading || configSaving}>
                                    {configSaving ? "Salvando..." : "Salvar configuracao"}
                                </Button>
                            </Box>
                        </form>
                    </Box>
                </Card>
            </Box>
            <Box display="flex" flexDirection="row" justifyContent="flex-start">
                <Typography color="#a18e38" fontSize={{ xs: "h7.fontSize", sm: "h6.fontSize", md: "h6.fontSize" }}>
                    Total: <b>{totalRows} Clientes</b>
                </Typography>
            </Box>
            <Box mt={3} mb={5}>
                <Card>
                    <DataTable
                        columns={columns}
                        data={data}
                        pagination
                        sortServer
                        onSort={handleSortChange}
                        progressPending={loading}
                        paginationServer
                        paginationTotalRows={totalRows}
                        onChangeRowsPerPage={handlePerRowsChange}
                        onChangePage={handlePageChange}
                        subHeader
                        subHeaderComponent={subHeaderComponentMemo}
                        persistTableHead
                    />
                </Card>
            </Box>

            <Box
                mb={1}
                mt={2}
                fontWeight="bold"
                color="#666666"
                fontSize={{ xs: "h7.fontSize", sm: "h6.fontSize", md: "h5.fontSize" }}
            >
                Historico de pontos e resgates
            </Box>
            <Typography variant="body2" color="text.secondary">
                Falhas de resgate ficam destacadas para auditoria, incluindo tentativas recusadas por codigo invalido, saldo insuficiente,
                limite excedido ou OS com desconto.
            </Typography>
            <Box mt={3} mb={5}>
                <Card>
                    <DataTable
                        columns={logColumns}
                        data={logData}
                        pagination
                        progressPending={logLoading}
                        paginationServer
                        paginationTotalRows={logTotalRows}
                        onChangeRowsPerPage={(newPerPage) => setLogPerPage(newPerPage)}
                        onChangePage={(newPage) => setLogPage(newPage - 1)}
                        conditionalRowStyles={logConditionalRowStyles}
                        persistTableHead
                        noDataComponent="Nenhum registro encontrado"
                    />
                </Card>
            </Box>
        </Container>
    );
};

export default ClientPoints;
